import React from 'react';
import InfoIcon from '@assets/icons/tooltip-icon.svg';
import { BaseSpin, BaseTooltip } from '@components';
import { ICON_COLORS } from '@constants/colors.ts';
import { useAppSelector } from '@hooks/reduxHooks.ts';
import { msg } from '@lingui/core/macro';
import { useLingui } from '@lingui/react/macro';
import { TooltipContent } from '@routes/___shared___/styled.tsx';
import CardProgressBar from '@routes/sidebar/___shared___/components/card-progress-bar';
import { Title } from '@routes/sidebar/learning/ui/dashboard-tab/___shared___/styled.tsx';
import {
    BaseAlertBannerWrapper,
    Container,
    Percentage,
    ProgressBarWrapper,
} from '@routes/sidebar/learning/ui/dashboard-tab/learning-compliance/styled.ts';
import { getPercentage } from '@shared/utils/numbers';
import { getMonthsAndDaysSinceString } from '@utils/date.ts';
import { selectServerTimeDate } from '@utils/serverTime.ts';
import { translateItem } from '@utils/translations.ts';
import type { AlertProps } from 'antd';

export const SHARED_LEARNING_COMPLIANCE_MANDATORY = msg`Mandatory learning compliance`;

export const LearningCompliance: React.FC = () => {
    const { t } = useLingui();
    const TOOLTIP_TITLE = t`Tracks progress in pathways marked as mandatory for your rank and vessel. Shows what’s completed and what remains.`;

    const serverTimeDate = useAppSelector(selectServerTimeDate) ?? new Date();

    const statisticsSummary = useAppSelector((state) => state.statistics.statisticsSummary);

    if (!statisticsSummary) {
        return <BaseSpin size="medium" />;
    }

    const { totalPathways, completedPathways, firstDueUTC, lastDueUTC, overduePathways } =
        statisticsSummary.mandatoryStatistics;
    const remainingPathways = totalPathways - completedPathways;
    const progressPercent = getPercentage(completedPathways, totalPathways);

    const startDate = firstDueUTC ? new Date(firstDueUTC) : null;
    const endDate = lastDueUTC ? new Date(lastDueUTC) : null;
    const now = serverTimeDate;

    const totalPeriodMs = startDate && endDate ? endDate.getTime() - startDate.getTime() : 0;
    const elapsedMs = startDate ? now.getTime() - startDate.getTime() : 0;
    const percentPeriodElapsed = totalPeriodMs > 0 ? getPercentage(totalPeriodMs, elapsedMs) : 0;

    const montsDaysRemaining = lastDueUTC
        ? getMonthsAndDaysSinceString(now.toISOString(), lastDueUTC)
        : {
              months: 0,
              days: 0,
          };

    let timeRemaining: string;
    const { months, days } = montsDaysRemaining;

    if (days <= 0 && months <= 0) {
        timeRemaining = t`0 days remaining`;
    } else if (months > 0 && days > 0) {
        if (months === 1 && days === 1) {
            timeRemaining = t`${months} month ${days} day`;
        } else if (months === 1) {
            timeRemaining = t`${months} month ${days} days`;
        } else if (days === 1) {
            timeRemaining = t`${months} months ${days} day`;
        } else {
            timeRemaining = t`${months} months ${days} days`;
        }
    } else if (months > 0) {
        timeRemaining = months === 1 ? t`${months} month` : t`${months} months`;
    } else {
        timeRemaining = days === 1 ? t`${days} day` : t`${days} days`;
    }

    let message = '';
    let bgColor = '';
    let alertType: AlertProps['type'] = 'warning';

    if (progressPercent >= 100) {
        if ((!endDate || now <= endDate) && overduePathways === 0) {
            message = t`You’re ahead of the game! Mandatory learning completed! Keep it up!`;
            bgColor = ICON_COLORS.success;
            alertType = 'success';
        } else {
            message = t`Mandatory learning completed, but some delays. Set goals to stay on track.`;
            bgColor = ICON_COLORS.warning;
        }
    } else if (percentPeriodElapsed <= 50) {
        message = t`You have ${remainingPathways} pathways and ${timeRemaining} to complete! Good luck!`;
    } else {
        if (progressPercent < 50) {
            message = t`You’re halfway through the allotted time with limited progress. Let’s accelerate!`;
        } else {
            message = t`You're ${remainingPathways} pathways away from completing your Mandatory learning!`;
        }
    }

    return (
        <Container>
            <Title>
                {translateItem(SHARED_LEARNING_COMPLIANCE_MANDATORY)}
                <BaseTooltip
                    title={<TooltipContent>{TOOLTIP_TITLE}</TooltipContent>}
                    placement="right"
                    arrow={false}
                    icon={<InfoIcon />}
                />
            </Title>
            <ProgressBarWrapper>
                <Percentage>{Math.round(progressPercent)}%</Percentage>
                <CardProgressBar
                    percentComplete={String(Math.round(progressPercent))}
                    minLeft={0}
                    isCustomText
                    progressColor={bgColor}
                />
            </ProgressBarWrapper>
            <BaseAlertBannerWrapper type={alertType} description={message} />
        </Container>
    );
};
